/**
 * Product Card Component
 *
 * Compact card for selecting a pharma product.
 * Shows key differentiators: brand name, target antigen, and indication.
 *
 * On desktop: entire card is pressable
 * On mobile (including mobile web): only the button is pressable, with "Learn more" link
 */

import { Crosshair, Dna, Info } from 'lucide-react-native';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { ProductLogo } from '../ProductLogo';

import { Badge, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { CheckIcon, Icon } from '@/components/ui/icon';
import { Link, LinkText } from '@/components/ui/link';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { Product, ProductId } from '@/constants/medicalOnDemand';

type ProductSelectCardProps = {
  product: Product;
  isSelected: boolean;
  isDisabled?: boolean;
  onSelect: () => void;
  onShowDetails?: () => void;
};

export function ProductSelectCard({
  product,
  isSelected,
  isDisabled = false,
  onSelect,
  onShowDetails,
}: ProductSelectCardProps) {
  const cardClassName = twJoin(
    'flex-1 rounded-lg border-2 bg-background-0 p-4 transition-all duration-200 ease-in-out',
    isSelected ? 'border-primary-500 bg-primary-50/40' : 'border-outline-200',
    isDisabled && 'opacity-50'
  );

  return (
    <VStack
      testID={`product-card-${product.id}`}
      className={cardClassName}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: isSelected }}
      accessibilityLabel={`${product.brandName} - ${product.genericName}`}
      accessibilityHint={`${product.drugClass}. ${product.indication}`}>
      <HStack className="gap-3">
        <View className="h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-outline-200 bg-white">
          <ProductLogo
            productId={product.id as ProductId}
            size={25}
            accessibilityLabel={`${product.brandName} logo`}
          />
        </View>

        <VStack className="min-w-0 flex-1 gap-1">
          <Text className="font-semibold text-typography-900">{product.brandName}</Text>
          <Text isTruncated numberOfLines={1} className="text-sm text-typography-500">
            {product.genericName}
          </Text>
        </VStack>
      </HStack>

      {/* Badges - mobile only */}
      <HStack className="mt-2 flex-wrap gap-1 md:hidden">
        {product.drugTarget && (
          <Badge
            size="sm"
            className="max-w-full shrink-0 gap-1"
            style={{ backgroundColor: `${product.branding.primaryColor}1A` }}>
            <Icon as={Crosshair} size="2xs" style={{ color: product.branding.primaryColor }} />
            <BadgeText
              className="text-xs"
              numberOfLines={1}
              style={{ color: product.branding.primaryColor }}>
              {product.drugTarget}
            </BadgeText>
          </Badge>
        )}
        {product.drugClass && (
          <Badge
            size="sm"
            className="max-w-full shrink-0 gap-1"
            style={{ backgroundColor: `${product.branding.secondaryColor}1A` }}>
            <Icon as={Dna} size="2xs" style={{ color: product.branding.secondaryColor }} />
            <BadgeText
              className="text-xs"
              numberOfLines={1}
              style={{ color: product.branding.secondaryColor }}>
              {product.drugClass}
            </BadgeText>
          </Badge>
        )}
      </HStack>

      <Text className="mt-3 flex-1 text-sm text-typography-700" numberOfLines={2}>
        {product.indication}
      </Text>

      {/* Learn More link - mobile only */}
      {onShowDetails && (
        <Link
          onPress={onShowDetails}
          className="mt-2 flex-row items-center gap-1 md:hidden"
          accessibilityLabel={`Learn more about ${product.brandName}`}
          accessibilityRole="button">
          <Icon as={Info} size="xs" className="text-primary-500" />
          <LinkText className="text-sm">Learn More</LinkText>
        </Link>
      )}

      <VStack className="mt-auto pt-4">
        <Pressable
          testID={`product-card-${product.id}-${isSelected ? 'deselect' : 'select'}`}
          onPress={onSelect}
          disabled={isDisabled}
          className={twJoin(
            'w-full flex-row items-center justify-between rounded-md border px-3 py-2',
            isSelected
              ? 'border-primary-500 bg-primary-50'
              : 'border-outline-300 bg-background-0 data-[hover=true]:border-outline-400'
          )}
          accessibilityLabel={`${isSelected ? 'Deselect' : 'Select'} ${product.brandName} product`}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: isSelected }}>
          <HStack className="items-center gap-2">
            <View
              className={twJoin(
                'h-5 w-5 items-center justify-center rounded-[4px] border-2',
                isSelected
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-outline-500 bg-background-0'
              )}>
              {isSelected ? <Icon as={CheckIcon} size="xs" className="text-typography-0" /> : null}
            </View>
            <Text
              className={twJoin(
                'font-medium text-sm',
                isSelected ? 'text-primary-700' : 'text-typography-800'
              )}>
              {isSelected ? 'Selected' : 'Select product'}
            </Text>
          </HStack>
          <Text
            className={twJoin('text-xs', isSelected ? 'text-primary-600' : 'text-typography-500')}>
            {isSelected ? 'Included' : 'Not selected'}
          </Text>
        </Pressable>
      </VStack>
    </VStack>
  );
}
